<template>
  <div class="select-cable-wrapper">
    <b-card
      class="select-card cable-selector-card"
      :class="{
        'border-danger margin-error': success_val === false
      }"
    >
      <div
        v-if="errorMessage"
        class="selector-alert"
      >
        <font-awesome-icon
          icon="circle-exclamation"
          class="me-2"
        />
        {{ errorMessage }}
      </div>

      <div class="cable-grid">
        <div
          v-for="option in cableOptions"
          :id="`cable_type_${option.type}`"
          :key="option.type"
          :class="[
            'cable-type',
            {
              'selected-cable': cableTypeSelected === option.type,
              'disabled-cable': isCableDisabled(option.type)
            }
          ]"
          :aria-disabled="isCableDisabled(option.type)"
          @click="selectCable(option.type)"
        >
          <div class="cable-icon-wrap">
            <img
              class="cable-img"
              :src="option.src"
              :alt="option.alt"
              :class="{ 'opacity-50': isCableDisabled(option.type) }"
            >
          </div>

          <div class="page-text cable-label">
            <div class="label-name">
              {{ option.labelName }}
            </div>
            <div class="label-lines">
              {{ option.labelLine }}
            </div>
          </div>
        </div>
      </div>

      <div class="selector-help">
        <p
          class="help-text"
          v-text="$t('cable_type.subtitle')"
        ></p>

        <div class="non-clickable">
          <img
            src="@/assets/images/cables/cable_opening_and_reverse.gif"
            alt="cabos na bateria"
          >
        </div>
      </div>

      <div v-if="!success_val">
        <div class="message-fail">
          <h5>{{ message }}</h5>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
  import { CABLE_TYPE_LIGHTNING, CABLE_TYPE_MICRO, CABLE_TYPE_USBC } from '@/constants.js'
  import { sessionRecorder, UserEvent, SESSION_EVENTS } from '@/services/sessionRecorder'
  import AppleImg from '@/assets/images/cables/apple_2.png'
  import MicroImg from '@/assets/images/cables/micro_2.png'
  import UsbCImg from '@/assets/images/cables/c_2.png'

  export default {
    name: 'SelectCableType',
    props: {
      userId: {
        type: Number,
        default: null
      },
      errorMessage: {
        type: String,
        default: ''
      },
      availableCableTypes: {
        type: Array, // Array of cable types (numbers) that are available. If empty/null, all assumed available.
        default: null
      },
      initialCableType: {
        type: Number,
        default: null
      }
    },
    emits: ['cableType'],
    data() {
      return {
        message: '',
        success_val: true,
        cableTypeSelected: null,
        isBlinking: false,
        CABLE_TYPE_LIGHTNING,
        CABLE_TYPE_MICRO,
        CABLE_TYPE_USBC
      }
    },
    computed: {
      cableOptions() {
        return [
          {
            type: this.CABLE_TYPE_LIGHTNING,
            src: AppleImg,
            alt: this.$t('cable_type.options.lightning.labelName'),
            labelName: this.$t('cable_type.options.lightning.labelName'),
            labelLine: this.$t('cable_type.options.lightning.labelLine')
          },
          {
            type: this.CABLE_TYPE_USBC,
            src: UsbCImg,
            alt: this.$t('cable_type.options.usbc.labelName'),
            labelName: this.$t('cable_type.options.usbc.labelName'),
            labelLine: this.$t('cable_type.options.usbc.labelLine')
          },
          {
            type: this.CABLE_TYPE_MICRO,
            src: MicroImg,
            alt: this.$t('cable_type.options.micro.labelName'),
            labelName: this.$t('cable_type.options.micro.labelName'),
            labelLine: this.$t('cable_type.options.micro.labelLine')
          }
        ]
      }
    },
    mounted() {
      if (this.initialCableType !== null) {
        this.cableTypeSelected = this.initialCableType
      }
    },
    methods: {
      isCableDisabled(cableType) {
        // Lightning cable (0) is always available in all powerbank types
        if (cableType === this.CABLE_TYPE_LIGHTNING) {
          return false
        }

        // If availableCableTypes is provided and not empty, check if this cableType is included
        if (this.availableCableTypes && this.availableCableTypes.length > 0) {
          return !this.availableCableTypes.includes(cableType)
        }
        return false
      },
      selectCable(cable) {
        if (this.isCableDisabled(cable)) {
          return
        }

        this.cableTypeSelected = cable
        let params = {
          cable_type: cable,
          user_id: this.userId
        }
        sessionRecorder.addUserEvent(new UserEvent(SESSION_EVENTS.CABLE_TYPE_SELECTION, null, params))

        // Emit immediately for better UX when changing
        this.$emit('cableType', cable)
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .select-cable-wrapper {
    width: 100%;
  }

  .cable-selector-card {
    background: #fff !important;
    border: 1px solid #edf2f7;
    border-radius: 16px !important;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08) !important;
    padding: 1rem !important;
  }

  .selector-alert {
    background: #fff5e8;
    color: #8a4b1f;
    border: 1px solid #ffd7ac;
    border-radius: 10px;
    padding: 0.625rem 0.875rem;
    margin-bottom: 0.875rem;
    font-size: 0.9rem;
    text-align: center;
  }

  .cable-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.625rem;
  }

  .cable-img {
    width: 100%;
    max-width: 72px;
    margin: 0 auto;
    opacity: 0.8;
    transition:
      transform 0.2s ease,
      opacity 0.2s ease;
  }

  .cable-icon-wrap {
    min-height: 78px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.25rem;
  }

  .cable-type {
    text-align: center;
    border: 1px solid #d6dbe1;
    border-radius: 12px;
    padding: 0.75rem 0.5rem;
    background: linear-gradient(180deg, #fff 0%, #fbfcfe 100%);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #f58a48;
      box-shadow: 0 4px 12px rgba(245, 138, 72, 0.12);
    }
  }

  .cable-label {
    line-height: 1.2;
  }

  .label-name {
    font-size: 0.86rem;
    font-weight: 700;
    color: #1f2937;
  }

  .label-lines {
    margin-top: 0.2rem;
    font-size: 0.72rem;
    color: #6b7280;
  }

  .selected-cable {
    border: 2px solid #f58a48;
    background: linear-gradient(180deg, rgba(245, 138, 72, 0.08) 0%, rgba(245, 138, 72, 0.03) 100%);
    box-shadow: 0 6px 14px rgba(245, 138, 72, 0.2);
    transform: translateY(-1px);

    .cable-img {
      opacity: 1;
      transform: scale(1.05);
    }
  }

  .disabled-cable {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: auto;

    &:hover {
      border-color: #d6dbe1;
      box-shadow: none;
    }
  }

  .selector-help {
    margin-top: 0.9rem;
    border-radius: 12px;
    border: 1px solid #edf2f7;
    background: #f8fafc;
    padding: 0.75rem;
  }

  .help-text {
    font-size: 0.78rem;
    text-align: center;
    color: #475569;
    margin-bottom: 0.45rem;
  }

  .margin-error {
    margin-bottom: 35px !important;
    padding-bottom: 35px !important;
  }

  .message-fail {
    width: 100%;
    padding: 0.45rem 0.8rem;
    text-align: center;
    line-height: 1;
    box-sizing: border-box;
    margin-top: 0.75rem;
    max-height: 2.25rem;
    overflow: hidden;
    font-size: 0.9rem;
    font-weight: bold;
    color: white;
    background-color: red;
    border-radius: 8px;
  }

  .non-clickable {
    opacity: 0.95;
    cursor: default;
    overflow: hidden;
    display: flex;
    justify-content: center;
  }

  .non-clickable img {
    display: block;
    margin-top: -2px;
    width: 195px;
    height: auto;
  }

  @media (width <= 576px) {
    .cable-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .cable-type {
      padding: 0.65rem 0.4rem;
    }
  }
</style>
